import type { RecordView } from '@kit/sync/types/record-view';
import { ListChecks } from 'lucide-react';
import { DOCUMENT_CATEGORY_LABELS } from '@/entities/document/config/document-category-labels';
import { documentDue } from '@/entities/document/helpers/document-due';
import type { KitDocument } from '@/entities/document/schemas/kit-document';
import { nextDue } from '@/entities/item/helpers/next-due';
import type { Item } from '@/entities/item/schemas/item';
import { CATEGORY_LABELS } from '@/entities/shared/category/category-labels';
import type { Due } from '@/entities/shared/due/due';
import { neededDue } from '@/entities/shopping-item/helpers/needed-due';
import type { ShoppingItem } from '@/entities/shopping-item/schemas/shopping-item';
import { REPEAT_UNIT_LABELS } from '@/entities/task/config/repeat-unit-labels';
import { TASK_LABELS } from '@/entities/task/config/task-labels';
import { taskDueOf } from '@/entities/task/helpers/task-due-of';
import type { Task } from '@/entities/task/schemas/task';
import { t } from '@/shared/i18n/t';
import { tn } from '@/shared/i18n/tn';
import { formatQuantity } from '@/shared/lib/money/format-quantity';
import type { AttentionRow } from '../schemas/attention-row';

type AttentionSources = {
  readonly items: readonly RecordView<Item>[];
  readonly docs: readonly RecordView<KitDocument>[];
  readonly tasks: readonly RecordView<Task>[];
  readonly shopping: readonly RecordView<ShoppingItem>[];
};

function calls(due: Due | null): due is Due {
  return due !== null && (due.urgency === 'soon' || due.urgency === 'overdue');
}

/** Everything late or inside its reminder window, overdue first, then by date. */
export function attentionRows({ items, docs, tasks, shopping }: AttentionSources): AttentionRow[] {
  const rows: AttentionRow[] = [];
  for (const view of items) {
    const due = nextDue(view.data);
    if (!calls(due)) continue;
    rows.push({
      id: view.id,
      to: `/inventory?edit=${view.id}`,
      title: view.data.name,
      subtitle: [formatQuantity(view.data.quantity, view.data.unit), view.data.location]
        .filter(Boolean)
        .join(' · '),
      icon: CATEGORY_LABELS[view.data.category].icon,
      due,
    });
  }
  for (const view of docs) {
    const due = documentDue(view.data);
    if (!calls(due)) continue;
    rows.push({
      id: view.id,
      to: `/documents?edit=${view.id}`,
      title: view.data.title,
      subtitle: view.data.holder ?? t(DOCUMENT_CATEGORY_LABELS[view.data.category].label),
      icon: DOCUMENT_CATEGORY_LABELS[view.data.category].icon,
      due,
    });
  }
  for (const view of tasks) {
    const due = taskDueOf(view.data);
    if (!calls(due)) continue;
    const { repeat_every: every } = view.data;
    const repeat = REPEAT_UNIT_LABELS[view.data.repeat_unit ?? 'months'];
    rows.push({
      id: view.id,
      to: `/tasks?edit=${view.id}`,
      title: view.data.title,
      subtitle: every ? tn(every, repeat.one, repeat.other) : t(TASK_LABELS.kind[view.data.kind]),
      icon: ListChecks,
      due,
      task: view,
    });
  }
  for (const view of shopping) {
    const due = neededDue(view.data);
    if (!calls(due)) continue;
    rows.push({
      id: view.id,
      to: `/shopping?edit=${view.id}`,
      title: view.data.name,
      subtitle: [formatQuantity(view.data.quantity, view.data.unit), view.data.store]
        .filter(Boolean)
        .join(' · '),
      icon: CATEGORY_LABELS[view.data.category].icon,
      due,
    });
  }
  const late = (row: AttentionRow) => (row.due.urgency === 'overdue' ? 0 : 1);
  return rows.sort((a, b) => late(a) - late(b) || a.due.date.localeCompare(b.due.date));
}
